import { Icon } from '@ps/isp-web-component-library';
import { AddLine } from '@ps/isp-web-component-library/icons/AddLine';
import { MinusLine } from '@ps/isp-web-component-library/icons/MinusLine';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation } from '@ps/web-shared-components';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import useLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';

import { TXTACL_Decrease, TXTACL_Increase } from './Counter.messages.json';
import styles from './Counter.scss';

interface Props {
  min: number;
  max: number;
  onChange: (score: number) => void;
  label: string;
}

const Counter: React.FC<Props> = ({ min, max, onChange, label }) => {
  const id = useRef(uniqueId('counter_'));
  const onChangeRef = useRef(onChange);
  const [score, setScore] = useState(min);
  const { inputFilled, buttonPressed } = useTrackers();

  useEffect(() => {
    if (score < min) {
      setScore(min);
    } else if (score > max) {
      setScore(max);
    }
  }, [min, max, score]);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  return (
    <>
      <label aria-labelledby={id.current} htmlFor={id.current}>
        {label}
      </label>
      <div className={styles.scoreCounter} role="group">
        <button
          className={styles.counterButton}
          aria-controls={id.current}
          type="button"
          onClick={() => {
            onChangeRef.current(score - 1);
            setScore((prevScore) => prevScore - 1);
            buttonPressed({ buttonName: 'correct_score_decrease' });
          }}
          disabled={score === min}
        >
          <VisuallyHidden.Root>
            <Translation {...TXTACL_Decrease} /> {label}
          </VisuallyHidden.Root>
          <Icon icon={MinusLine} size="sm" />
        </button>
        <input
          id={id.current}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          onChange={(evt) => {
            let newScore = Number(evt.target.value);

            if (Number.isNaN(newScore)) return;

            if (newScore > max) {
              newScore = max;
            } else if (newScore < min) {
              newScore = min;
            }

            onChangeRef.current(newScore);
            setScore(newScore);
            inputFilled({ fieldType: 'text', fieldName: 'correct_score_input' }, evt.target.value);
          }}
          aria-valuenow={score}
          aria-valuetext={String(score)}
          aria-valuemin={min}
          aria-valuemax={max}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={score}
          disabled={score === min && score === max}
        />
        <button
          className={styles.counterButton}
          aria-controls={id.current}
          type="button"
          onClick={() => {
            onChangeRef.current(score + 1);
            setScore((prevScore) => prevScore + 1);
            buttonPressed({ buttonName: 'correct_score_increase' });
          }}
          disabled={score === max}
        >
          <VisuallyHidden.Root>
            <Translation {...TXTACL_Increase} /> {label}
          </VisuallyHidden.Root>
          <Icon icon={AddLine} size="sm" />
        </button>
      </div>
    </>
  );
};

export default Counter;
